import { useEffect, useState, useCallback } from 'react';
import { Feather } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ONBOARDING_EVENTS } from '@/constants/onboardingConfig';
import FeedbackModal from './FeedbackModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function Screen5Signup({ onBack, preview, sessionId, trackEvent }) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackTrigger, setFeedbackTrigger] = useState('back_button');

  useEffect(() => {
    trackEvent(ONBOARDING_EVENTS.SIGNUP_VIEWED);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Push a history entry so browser back triggers popstate instead of leaving
  useEffect(() => {
    window.history.pushState({ onboarding: true }, '', window.location.href);

    const handlePopState = (e) => {
      // Restore the history entry and show the feedback modal
      window.history.pushState({ onboarding: true }, '', window.location.href);
      setFeedbackTrigger('browser_back');
      setShowFeedback(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Track tab close / hide as abandonment (cannot show modal here)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackEvent(ONBOARDING_EVENTS.ABANDONED, { trigger: 'tab_hidden' });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [trackEvent]);

  const handleBackClick = useCallback(() => {
    setFeedbackTrigger('back_button');
    setShowFeedback(true);
  }, []);

  const handleGoogleSignIn = async () => {
    trackEvent(ONBOARDING_EVENTS.SIGNUP_COMPLETED, { method: 'google' });
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: `${BACKEND_URL}/api/auth/google?native=true` });
        return;
      }
    } catch {}
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const handleFeedbackStay = () => setShowFeedback(false);

  const handleFeedbackLeave = () => {
    setShowFeedback(false);
    trackEvent(ONBOARDING_EVENTS.ABANDONED, { trigger: feedbackTrigger });
    onBack();
  };

  return (
    <div className="story-page flex flex-col fade-in">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 max-w-sm mx-auto w-full text-center">
        <Feather className="w-8 h-8 text-[var(--story-primary)] mb-6" />

        {/* Mini preview card — the carrot */}
        {preview?.title && (
          <div className="story-surface w-full p-5 mb-8 text-left">
            <p className="story-label mb-2">
              Your story
            </p>
            <p className="font-serif text-base font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-1">
              {preview.title}
            </p>
            {preview.preview && (
              <p className="font-serif text-sm italic text-[#6B6560] dark:text-[#9C9590] leading-relaxed line-clamp-2">
                {preview.preview}
              </p>
            )}
          </div>
        )}

        <h2 className="story-title text-2xl sm:text-3xl mb-3">
          Your story has started.
        </h2>
        <p className="story-body text-sm mb-8">
          Save your memory and continue building your book.
        </p>

        {/* Google sign-in — matches LoginPage pattern */}
        <Button
          onClick={handleGoogleSignIn}
          className="story-button-secondary w-full flex items-center justify-center gap-3 mb-4"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </Button>

        <p className="text-xs text-[#A09890] dark:text-[#5C5650] leading-relaxed">
          Free to begin. By continuing you agree to our{' '}
          <a href="/terms" className="underline hover:text-[#5C4D6B] dark:hover:text-[#B0A0C0] transition-colors">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy" className="underline hover:text-[#5C4D6B] dark:hover:text-[#B0A0C0] transition-colors">
            Privacy Policy
          </a>
          .
        </p>
      </div>

      {/* Back link — triggers feedback modal */}
      <div className="shrink-0 text-center pb-6">
        <button
          onClick={handleBackClick}
          className="text-sm text-[#A09890] dark:text-[#5C5650] hover:text-[#6B6560] dark:hover:text-[#9C9590] transition-colors"
        >
          ← Go back
        </button>
      </div>

      <FeedbackModal
        isOpen={showFeedback}
        onStay={handleFeedbackStay}
        onLeave={handleFeedbackLeave}
        sessionId={sessionId}
        trigger={feedbackTrigger}
        trackEvent={trackEvent}
      />
    </div>
  );
}
